import { Link } from "react-router-dom";
import { Waves, Shapes, ArrowRight } from "lucide-react";
import { Reveal, ChapterHeader } from "./Reveal";

const HALVES = [
  {
    icon: Waves,
    title: "Flow",
    sub: "The philosophical half",
    text: "Moving with your own pace and material rather than a fixed programme, trusting that a story followed honestly tends toward its own kind of order. Change works with the nature of a situation instead of against it.",
  },
  {
    icon: Shapes,
    title: "Form",
    sub: "The psychological half",
    text: "The scaffolding that makes flow safe: the therapeutic relationship, Gestalt play and CBT techniques, structured assessment, and the ethical container of HPCSA registration, training and supervision.",
  },
];

export const TheName = () => (
  <section id="the-name" data-testid="the-name-section" className="py-24 md:py-32 bg-pawc-mist/50">
    <div className="max-w-6xl mx-auto px-6">
      <ChapterHeader number="02" eyebrow="The Name" title="Why Yielding: Flow & Form" />

      <div className="grid lg:grid-cols-12 gap-12 mt-12">
        <div className="lg:col-span-5 max-w-[65ch] space-y-5 text-pawc-slate text-base md:text-lg leading-relaxed">
          <Reveal>
            <p>
              In the Tao Te Ching, Laozi returns again and again to water as the
              model for how change actually happens. Water does not force its way
              through stone. It wears stone down simply by continuing to move,
              taking the shape of whatever it meets. The person who yields is not
              weak; they are the one still standing when the rigid thing has
              snapped.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p>
              So much of what brings people to therapy is a kind of fighting:
              fighting a feeling, a memory, a version of themselves they were told
              to be. Yielding is not resignation. It is the willingness to stop
              bracing against what is actually true, so that real movement becomes
              possible. You cannot be carried by a current you are still trying to
              stand still against.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-sm md:text-base text-pawc-slate/90">
              This philosophy meets the clinical room through narrative therapy,
              where your story is a current rather than a monument; through Yalom's
              existential psychotherapy; through Gestalt's attention to the living
              shape of present experience; and through Alan Watts's lifelong lesson:
              the aim is not to stop the water, but to stop fighting it.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="grid sm:grid-cols-2 gap-6">
            {HALVES.map((h, i) => (
              <Reveal key={h.title} delay={0.1 + i * 0.1}>
                <article
                  data-testid={`name-card-${h.title.toLowerCase()}`}
                  className="h-full rounded-2xl bg-pawc-sand border border-pawc-teal/10 p-8 hover:-translate-y-1 hover:shadow-[0_25px_50px_-12px_rgba(14,95,115,0.12)] transition-[transform,box-shadow] duration-500"
                >
                  <span className="inline-flex w-11 h-11 rounded-full bg-pawc-teal/10 text-pawc-teal items-center justify-center mb-5">
                    <h.icon size={20} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-3xl text-pawc-deep">{h.title}</h3>
                  <p className="text-xs tracking-[0.25em] uppercase text-pawc-terra mt-1 mb-4">
                    {h.sub}
                  </p>
                  <p className="text-sm md:text-base text-pawc-slate leading-relaxed">{h.text}</p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.25}>
            <blockquote
              data-testid="name-pull-quote"
              className="rounded-2xl bg-pawc-deep text-pawc-sand p-8 md:p-10"
            >
              <p className="font-serif italic text-xl md:text-2xl leading-relaxed">
                "Yielding is the willingness to stop resisting what is true. Flow
                and form are how that willingness becomes something you can
                actually stand on."
              </p>
            </blockquote>
            <Link
              data-testid="read-full-story-link"
              to="/story"
              className="inline-flex items-center gap-2 text-sm text-pawc-teal hover:text-pawc-deep transition-colors duration-300 mt-6"
            >
              Read the full story behind the name <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
